/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { AllContentCard, AllContentPage, AllContentPlaylist } from '../../../../shared/models/all-content/all-content.model';
import { Page } from '../../../../shared/models/page';
import { PlaylistPublishedStatus } from '../../../../shared/models/playlist/learner-playlist-summary.model';
import { RestClientService } from '../../../../shared/services/rest-client.service';
import { ObservableResult } from '../../../../shared/store';
import { AllContentDataService } from './all-content-data.service';

@Injectable()
export class ApiAllContentDataService implements AllContentDataService {
  constructor(
    private client: RestClientService,
    private translocoService: TranslocoService,
  ) {}

  loadAllPages(page: number, size: number, term: string): ObservableResult<Page<AllContentPage>> {
    const params = {
      page: page.toString(),
      size: size.toString(),
    };
    if (term) {
      params['term'] = term;
    }
    return this.client.get<Page<AllContentPage>>(Location.joinWithSlash(environment.apiRootUrl || '', 'pages/list'), params).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetAllContentPages'))),
    );
  }

  loadAllPlaylists(
    page: number,
    size: number,
    term: string,
    types?: string[],
    status?: PlaylistPublishedStatus,
    parentUids?: string[],
  ): ObservableResult<Page<AllContentPlaylist>> {
    const params = {
      page: page.toString(),
      size: size.toString(),
    };
    if (term) {
      params['term'] = term;
    }
    if (types?.length) {
      params['types'] = types.join(',');
    }
    if (status) {
      params['status'] = status;
    }
    if (parentUids?.length) {
      params['parentUids'] = parentUids.join(',');
    }
    return this.client.get<Page<AllContentPlaylist>>(Location.joinWithSlash(environment.apiRootUrl || '', 'playlists/list'), params).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetAllContentPlaylists'))),
    );
  }

  loadAllCards(page: number, size: number, term: string): ObservableResult<Page<AllContentCard>> {
    const params = {
      page: page.toString(),
      size: size.toString(),
    };
    if (term) {
      params['term'] = term;
    }
    return this.client.get<Page<AllContentCard>>(Location.joinWithSlash(environment.apiRootUrl || '', 'cards/list'), params).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetAllContentCards'))),
    );
  }
}
